import { logger } from "@src/shared/helpers/logger";
import { isFunction } from "radashi";
import { engine } from "..";
import { toArray } from "../vector";

/**
 * Helper function to prevent memory leaks by tracking live objects.
 * It wraps the target object in a Proxy and monitors method calls to check
 * for discrepancies in the number of live objects before and after the call.
 */
export function trackLiveObjects<T extends object>(
  repository: string,
  target: T,
): T {
  return new Proxy(target, {
    get(obj, prop, receiver) {
      const value = Reflect.get(obj, prop, receiver);

      if (isFunction(value)) {
        return (...args: unknown[]) => {
          const t0 = performance.now();
          const nbStartLiveObjects = engine.getNumberLiveObjects();
          let result;
          try {
            result = value.apply(obj, args);
          } catch (error) {
            logger.error(
              `Error when executing ${repository}.${prop.toString()}(${args.map((value) => JSON.stringify(value)).join(", ")})`,
              error,
            );
            throw error;
          }
          const nbEndLiveObjects = engine.getNumberLiveObjects();
          if (nbStartLiveObjects < nbEndLiveObjects) {
            printPossibleLeak(
              repository,
              prop.toString(),
              nbStartLiveObjects,
              nbEndLiveObjects,
            );
          }
          const t1 = performance.now();
          if (t1 - t0 > 100) {
            logger.debug(
              `${repository}.${prop.toString()} took ${t1 - t0} ms to execute.`,
            );
          }
          return result;
        };
      }

      return value;
    },
  });
}

function printPossibleLeak(
  repository: string,
  methodName: string,
  nbStart: number,
  nbEnd: number,
) {
  // Ignore methods that start with "init" as they are expected to create live objects.
  if (/^init.*/.test(methodName)) return;
  logger.warn(
    `Possible memory leak detected in  ${repository}.${methodName}: started with ${nbStart} and ended with ${nbEnd} objects.
 Live object classes:
    ${toArray(engine.getLiveObjectsClassName())
      .map((curr) => `- ${curr.toString()}`)
      .join("\t")}`,
  );
}
